import React from 'react';
import { motion } from 'framer-motion';
import NavigationExact from '../components/NavigationExact';
import FooterExact from '../components/FooterExact';

const RiskDisclosurePage = () => {
  return (
    <>
      <NavigationExact />
      <main className="pt-16 bg-[#FAFAFA]">
        <section className="py-16 bg-gradient-to-br from-gray-50 via-white to-[#f7f5f2]">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center"
            >
              <p className="text-sm uppercase tracking-[0.32em] text-[#5C1F33] mb-4">Risk Disclosure</p>
              <h1 className="text-4xl sm:text-5xl font-extrabold text-slate-900 mb-6">Risk Disclosure Statement</h1>
              <p className="mx-auto max-w-3xl text-lg text-slate-600 leading-8">
                IMPORTANT NOTICE: Investing and lending involve significant financial risks. Please read this Risk Disclosure Statement carefully before using FundingSathi's services. The risks described herein are not exhaustive.
              </p>
            </motion.div>
          </div>
        </section>

        <section className="py-12">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 space-y-10 text-slate-700">

            <div className="space-y-6">
              <div>
                <h2 className="text-2xl font-semibold text-slate-900 mb-4">Introduction</h2>
                <p className="text-slate-600">This Risk Disclosure Statement ("Statement") is issued by FundingSathi ("FundingSathi", "we", "us" or "our") in accordance with applicable laws and regulations governing financial services in India. The purpose of this Statement is to ensure that investors and users of our platform understand the nature and extent of risks involved in participating in business loan facilitation through www.fundingsathi.com ("Website" or "Platform").</p>
                <p className="text-slate-600 mt-3">By accessing or using the FundingSathi Platform, you acknowledge that you have read, understood and accepted the risks set out in this Statement. This Statement is not intended to be exhaustive and does not constitute financial, legal, or tax advice. You are strongly encouraged to seek independent professional advice before making any financial decisions.</p>
              </div>

              <div>
                <h2 className="text-2xl font-semibold text-slate-900 mb-4">Who This Statement Applies To</h2>
                <p className="text-slate-600">This Risk Disclosure Statement applies to all individual investors and users who register on the FundingSathi Platform, browse or access information about investment or lending opportunities, participate in funding business loans through the Platform, or use any other financial services offered by FundingSathi.</p>
              </div>

              <div>
                <h2 className="text-2xl font-semibold text-slate-900 mb-4">Nature of FundingSathi's Services</h2>
                <p className="text-slate-600">FundingSathi is an online financial platform that facilitates business loans by connecting individual investors with businesses seeking funding. FundingSathi acts as an intermediary and does not guarantee returns, repayment of principal, or any specific financial outcome. FundingSathi is not a bank, non-banking financial company (NBFC), or deposit-taking institution.</p>
              </div>

              <div>
                <h2 className="text-2xl font-semibold text-slate-900 mb-4">Risk Factors</h2>
                <ol className="list-decimal list-inside space-y-2 text-slate-600">
                  <li><strong>Risk of Capital Loss</strong> — You may lose part or all of your invested capital.</li>
                  <li><strong>Credit Risk / Default Risk</strong> — Borrowers may fail to repay principal or interest on time or at all.</li>
                  <li><strong>Liquidity Risk</strong> — Investments are typically illiquid and may not be exitable before loan tenure ends.</li>
                  <li><strong>Interest Rate Risk</strong> — Changes in market interest rates may affect the relative attractiveness of your returns.</li>
                  <li><strong>Concentration Risk</strong> — Lack of diversification increases exposure to default risk.</li>
                  <li><strong>Platform / Operational Risk</strong> — Technical failures, cyber-attacks, system outages or disruptions may affect the Platform.</li>
                  <li><strong>Regulatory and Legal Risk</strong> — Changes in laws or regulations may affect the operation of the Platform.</li>
                  <li><strong>Fraud and Misrepresentation Risk</strong> — Borrowers may provide false or misleading information.</li>
                  <li><strong>Macroeconomic Risk</strong> — Economic conditions may adversely affect borrowers' ability to repay loans.</li>
                  <li><strong>Tax Risk</strong> — Returns may be subject to taxes; consult a qualified tax advisor.</li>
                  <li><strong>Force Majeure Risk</strong> — Events beyond control may disrupt repayments or Platform operations.</li>
                  <li><strong>Risk of Platform Discontinuation</strong> — In the event FundingSathi ceases operations, recovery of outstanding funds cannot be guaranteed.</li>
                </ol>
              </div>

              <div>
                <h2 className="text-2xl font-semibold text-slate-900 mb-4">No Guarantee of Returns</h2>
                <p className="text-slate-600">FundingSathi does not guarantee any minimum return or protection of capital. All projections are estimates and not assurances of future performance.</p>
              </div>

              <div>
                <h2 className="text-2xl font-semibold text-slate-900 mb-4">Investor Responsibilities</h2>
                <p className="text-slate-600">Investors are responsible for assessing their own financial situation, conducting independent due diligence, diversifying portfolios, securing account credentials, and seeking appropriate professional advice.</p>
              </div>

              <div>
                <h2 className="text-2xl font-semibold text-slate-900 mb-4">Suitability</h2>
                <p className="text-slate-600">The Platform may not be suitable for all investors. Consider your financial resources, risk appetite, and time horizon before investing.</p>
              </div>

              <div>
                <h2 className="text-2xl font-semibold text-slate-900 mb-4">Limitation of Liability</h2>
                <p className="text-slate-600">FundingSathi shall not be liable for any direct or indirect losses arising from use of the Platform. To the maximum extent permitted by law, FundingSathi's total liability shall not exceed fees paid by you related to the specific transaction.</p>
              </div>

              <div>
                <h2 className="text-2xl font-semibold text-slate-900 mb-4">Amendments</h2>
                <p className="text-slate-600">FundingSathi may update this Statement at any time; the updated version will be posted on the Website and take effect immediately upon posting.</p>
              </div>

              <div>
                <h2 className="text-2xl font-semibold text-slate-900 mb-4">Contact Us</h2>
                <p className="text-slate-600">If you have any questions about this Risk Disclosure Statement, please contact us at:</p>
                <ul className="list-disc list-inside mt-3 space-y-2 text-slate-600">
                  <li>Email: <a href="mailto:support@fundingsathi.com" className="text-[#5C1F33] underline">support@fundingsathi.com</a></li>
                  <li>Website: <a href="https://www.fundingsathi.com" className="text-[#5C1F33] underline">www.fundingsathi.com</a></li>
                </ul>
              </div>

            </div>

          </div>
        </section>
      </main>
      <FooterExact />
    </>
  );
};

export default RiskDisclosurePage;
